import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import {
  Box, Text, Select, Divider,
} from '@mantine/core';
import { useSelector } from 'react-redux';
import {
  IconSparkles, IconLock, IconPencil, IconShieldCheck, IconRefresh,
} from '@tabler/icons-react';
import AmountSlider from '@/components/AmountSlider';
import styles from './mortgageRecommendation.module.css';
import { MortgageInfoBoxes } from '..';
import { useCalculateCycleMortgageWithSamePeriodMutation } from '@/services/api';

const format = (value, shouldRound = true) => {
  if (value === undefined) return '---';
  if (typeof value === 'number') {
    return shouldRound
      ? Math.round(value).toLocaleString()
      : value.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
  }
  return value;
};

// Helpers – hoisted out of the component since they don't use any closure state
const sumTotalAmount = (loans) => loans.reduce((sum, l) => sum + Number(l.totalAmount || 0), 0);
const maxMonthsLeft = (loans) => (loans.length > 0
  ? Math.max(...loans.map((l) => l.monthsLeft || 0))
  : 0);

const MortgageRecommendation = (props) => {
  const {
    data,
    period,
    loanAmount,
    onPeriodChange,
    onRiskChange,
    selectedRisk,
    getMix,
    morgageCycleDetails,
    proposal,
  } = props;

  const { code } = useSelector((state) => state.user?.role);
  const { extraDetails } = proposal;

  const { isRecycling, loanTypeName } = extraDetails || {};
  const { currentLoans, calculationData, comparisonData } = morgageCycleDetails || {};
  const { totalsAfterCalculation, totalsBeforeCalculation } = calculationData || {};

  let additionalSavingsAmount = 0;
  if (totalsAfterCalculation && totalsBeforeCalculation && loanTypeName === 'anyPurposeWithPropertyCollateral') {
    const { finalPayment: beforeFinalPayment } = totalsBeforeCalculation;
    const { finalPayment: afterFinalPayment } = totalsAfterCalculation;
    additionalSavingsAmount += beforeFinalPayment - afterFinalPayment;
  }

  const isRecycled = isRecycling === 'yes';
  const risks = data.map((d) => d.risk);
  let updatedLoanAmount = loanAmount;
  const recycledLoansComments = [];
  let unchangedLoans = [];
  let recycledLoans = [];
  // Get the selected risk (default to lowest risk) - must be before early returns
  let sortedRisks = [...risks].sort((a, b) => Number(a) - Number(b));
  if (isRecycled) {
    sortedRisks = [sortedRisks[0]];
  }

  if (isRecycled && comparisonData) {
    let loansToAddToLoan = comparisonData.filter((l) => l.isBetterToChange === true);
    let loanToCycle = comparisonData.filter((l) => l.isBetterToChange === false);

    if (loanTypeName === 'anyPurposeWithPropertyCollateral') {
      loansToAddToLoan = comparisonData.filter((l) => l.isBetterToChange === true && l.loanPurpose === 'אחר');
      loanToCycle = comparisonData.filter((l) => l.isBetterToChange === false || l.loanPurpose === 'דיור');
    }

    unchangedLoans = loanToCycle;
    recycledLoans = loansToAddToLoan;
  }

  const fixedLoans = unchangedLoans.filter((l) => l.isBetterToChange !== true);
  const recycledSamePeriodLoans = unchangedLoans.filter((l) => l.isBetterToChange === true);
  const hasRecycledSamePeriod = recycledSamePeriodLoans.length > 0;

  // Local state for period if parent doesn't provide onPeriodChange
  const [localPeriod, setLocalPeriod] = useState(period);
  const [samePeriodResult, setSamePeriodResult] = useState(null);
  const [calculateCycleMortgageWithSamePeriod] = useCalculateCycleMortgageWithSamePeriodMutation();

  // Stable fingerprint so the effect doesn't refire on every render
  const recycledSamePeriodFingerprint = recycledSamePeriodLoans
    .map((l) => `${l.loanNumber}-${l.totalAmount}`)
    .join(',');

  useEffect(() => {
    if (!hasRecycledSamePeriod) {
      setSamePeriodResult(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const result = await calculateCycleMortgageWithSamePeriod({
          loans: recycledSamePeriodLoans,
          isMergingLoans: true,
        }).unwrap();

        if (!cancelled) setSamePeriodResult(result.data);
      } catch (err) {
        console.error('Failed to calculate same-period cycle', err);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recycledSamePeriodFingerprint]);

  if (risks.length === 0) return null;

  if (risks.length === 1 && !getMix(risks[0])) return null;

  // Get the selected risk group
  const selectedRiskGroup = data.find((group) => group.risk === selectedRisk);

  // Use the min and max periods from the selected risk group
  const minPeriod = selectedRiskGroup?.minPeriod || 5;
  const maxPeriod = selectedRiskGroup?.maxPeriod || 30;

  // Ensure current period is within the new range
  const currentPeriod = onPeriodChange ? period : localPeriod;
  if (currentPeriod < minPeriod || currentPeriod > maxPeriod) {
    const newPeriod = Math.max(minPeriod, Math.min(maxPeriod, currentPeriod));
    if (onPeriodChange) {
      onPeriodChange(newPeriod);
    } else {
      setLocalPeriod(newPeriod);
    }
  }

  // Create marks for the slider based on selected risk
  const marks = [];
  for (let i = minPeriod; i <= maxPeriod; i += 5) {
    marks.push({ value: i, label: `${i} שנים` });
  }

  // Get the selected mix
  const selectedMix = getMix(selectedRisk);

  // Create risk options for the select
  const getRiskLabel = (risk) => {
    if (risk === sortedRisks[0]) return code === '5' ? '⭐ תמהיל מומלץ' : '⭐ תמהיל מומלץ Credit Pro';
    return 'תמהיל בסיכון גבוה יותר';
  };

  const riskOptions = sortedRisks.map((risk) => ({
    value: risk.toString(),
    label: getRiskLabel(risk),
  }));

  let monthlyPayment = selectedMix?.utilizedRepaymentCapacity ? selectedMix.utilizedRepaymentCapacity : '---';
  let totalPayment = selectedMix?.totalReturn ? selectedMix.totalReturn : '---';
  let returnConversion = selectedMix?.returnConversion ? selectedMix.returnConversion : '---';

  // Totals for the two sub-sections of the existing card
  const fixedTotalAmount = sumTotalAmount(fixedLoans);
  // eslint-disable-next-line max-len
  const fixedMonthlyPayment = fixedLoans.reduce((sum, l) => sum + Number(l.monthlyPayment || 0), 0) || 0;
  const fixedMaxMonthsLeft = maxMonthsLeft(fixedLoans);

  const recycledSamePeriodTotalAmount = sumTotalAmount(recycledSamePeriodLoans);
  // eslint-disable-next-line max-len
  const recycledSamePeriodMonthlyPayment = samePeriodResult?.totalsAfterCalculation?.newMonthlyPayment || 0;
  const recycledSamePeriodMaxMonthsLeft = maxMonthsLeft(recycledSamePeriodLoans);

  const recycledTotalAmount = recycledLoans.reduce((sum, l) => sum + Number(l.totalAmount || 0), 0);
  const newAndRecycledAmount = loanAmount + recycledTotalAmount;

  if (isRecycled) {
    const recycledTotal = currentLoans.reduce((sum, loan) => sum + Number(loan.totalAmount), 0);
    updatedLoanAmount += recycledTotal;

    // eslint-disable-next-line max-len
    monthlyPayment = Number(monthlyPayment) + recycledSamePeriodMonthlyPayment + fixedMonthlyPayment;
    totalPayment += totalsAfterCalculation.finalPayment;
    returnConversion = (totalPayment / updatedLoanAmount).toFixed(2);

    if (recycledLoans.length > 0) {
      recycledLoansComments.push('.הצעה זו כוללת המלצה למחזור חלק מהלוואות המקוריות  אשר תנאי הריבית שלהן ניתנים לשיפור');
    }
  }

  const renderExistingSubSection = ({
    badgeClass, BadgeIcon, badgeText,
    title, description, amount, amountCaption,
    monthlyPayment: subMonthlyPayment, monthsLeft,
    compact = false,
  }) => (
    <>
      <Box className={styles.innerCard}>
        <Box className={badgeClass}>
          <BadgeIcon size={13} />
          <span>{badgeText}</span>
        </Box>
      </Box>
      <Text fw={700} size={compact ? 'md' : 'lg'} ta='right' mb={compact ? 1 : 4}>{title}</Text>
      <Text size='sm' c='dimmed' ta='right' mb={compact ? 'xs' : 'lg'}>{description}</Text>
      <Text size='xl' fw={800} > {format(amount)} ₪</Text>
      <Text size='sm' c='dimmed' mb={compact ? 'xs' : 'lg'}>{amountCaption}</Text>
      <Box className={styles.cardRows}>
        <Box className={styles.cardRow}>
          <Text size='sm'>החזר חודשי נוכחי</Text>
          <Text size='sm' fw={600}> {format(subMonthlyPayment)} ₪</Text>
        </Box>
        <Box className={styles.cardRow}>
          <Text size='sm'>תקופה שנותרה</Text>
          <Text size='sm' fw={600}>
            {Math.floor(monthsLeft / 12)} שנים
            {monthsLeft % 12 > 0 && ` ו-${monthsLeft % 12} חודשים`}
          </Text>
        </Box>
      </Box>
    </>
  );

  const headerTitle = isRecycled ? 'המלצה למשכנתא כוללת בגובה:' : 'המלצה למשכנתא בגובה:';
  return (
    <Box className={styles.container}>

      <MortgageInfoBoxes
        boxes={[
          {
            title: 'סכום כולל של ההלוואה',
            value: `${format(updatedLoanAmount)} ₪`,
            borderColor: '#00ABAB',
          },
          {
            title: 'החזר חודשי כולל התחלתי',
            value: `${format(monthlyPayment)} ₪`,
            borderColor: '#00ABAB',
          },
          {
            title: 'סך ההחזר הכולל לסוף התקופה',
            value: `${format(totalPayment)} ₪`,
            borderColor: '#00ABAB',
          },
          {
            title: 'עבור כל שקל הלקוחות ישלמו',
            value: `${format(returnConversion, false)} ₪`,
            borderColor: '#00ABAB',
          },
        ]}
      />

      {isRecycled && (
        <Box className={styles.mortgageCards}>
          {/* Right card - existing/unchanged (with optional recycled-same-period sub-section) */}
          <Box className={styles.existingCard}>
            { renderExistingSubSection({
              badgeClass: styles.cardBadgeGray,
              BadgeIcon: IconLock,
              badgeText: 'לא מומלץ לשינוי',
              title: 'מסלולים קיימים ללא שינוי',
              description: 'חלק זה של המשכנתא נשאר בתנאים המקוריים שלו.',
              amount: fixedTotalAmount,
              amountCaption: 'יתרה נוכחית לא ממוחזרת',
              monthlyPayment: fixedMonthlyPayment,
              monthsLeft: fixedMaxMonthsLeft,
              compact: hasRecycledSamePeriod && fixedLoans.length > 0,
            })}

            {hasRecycledSamePeriod && fixedLoans.length > 0 && <Divider my='lg' />}

            {hasRecycledSamePeriod && renderExistingSubSection({
              badgeClass: styles.cardBadgeTeal,
              BadgeIcon: IconRefresh,
              badgeText: 'ללא שינוי תקופה',
              title: 'מסלולים למחזור',
              description: 'חלק זה של המשכנתא ממוחזר בתנאי ריבית טובים יותר ללא שינוי תקופה',
              amount: recycledSamePeriodTotalAmount,
              amountCaption: 'יתרה ממוחזרת בתנאים משופרים',
              monthlyPayment: recycledSamePeriodMonthlyPayment,
              monthsLeft: recycledSamePeriodMaxMonthsLeft,
              compact: hasRecycledSamePeriod && fixedLoans.length > 0,
            })}

            {!(hasRecycledSamePeriod && fixedLoans.length > 0) && (
            <Box className={styles.cardShield}>
              <IconShieldCheck size={36} />
            </Box>
            )}
          </Box>

          {/* Left card - new & recycled */}
          <Box className={styles.newCard} >
            <Box className={styles.innerCard}>
              <Box className={styles.cardBadgeTeal}>
                <IconPencil size={13} />
                <span>ניתן להתאמה</span>
              </Box>
            </Box>
            <Text fw={700} size='lg' ta='right' mb={4}>מסלולים חדשים וממוחזרים</Text>
            <Text size='sm' c='dimmed' ta='right' mb='lg'>התאם את תקופת ההלוואה כדי למקסם את החיסכון החודשי.</Text>
            <Text size='xl' fw={800}> {format(newAndRecycledAmount)} ₪</Text>
            <Text size='sm' c='dimmed' mb='lg'>סכום מיחזור ותוספת</Text>
            <Box className={styles.cardRows}>
              <Box className={styles.cardRow}>
                <Text size='sm'>החזר חודשי התחלתי</Text>
                <Text size='sm' fw={600}> {format(selectedMix?.utilizedRepaymentCapacity)} ₪</Text>
              </Box>
              <Box className={styles.cardRow}>
                <Text size='sm'>תקופת ההלוואה החדשה</Text>
                <Text size='sm' fw={600}>{currentPeriod} שנים</Text>
              </Box>
            </Box>
            <Box mt='md'>
              <AmountSlider
                value={currentPeriod}
                onChange={(value) => {
                  const numericValue = Number(String(value).replace(/[^\d]/g, ''));
                  if (onPeriodChange) onPeriodChange(numericValue);
                  else setLocalPeriod(numericValue);
                }}
                min={minPeriod}
                max={maxPeriod}
                step={1}
                showValue={false}
                showYearBox={false}
                marksStep={5}
              />
            </Box>
          </Box>

        </Box>
      )}
      { !isRecycled && (
      <>
        {/* Header with Recommendation Amount */}
        <Box className={styles.header}>
          <Text className={styles.headerTitle}>
            {headerTitle}
          </Text>
          <Text className={styles.headerAmount}>
            {format(updatedLoanAmount)} ₪
          </Text>
        </Box>

        {/* Risk Selection */}
        {risks.length > 1 && (
        <Box className={styles.riskSelection}>
          <Text className={styles.riskLabel}>
            מסלול משכנתא:
          </Text>
          <Select
            value={selectedRisk?.toString()}
            onChange={(value) => {
              onRiskChange(value);
            }}
            data={riskOptions}
            size="md"
          />
        </Box>
        )}

        {/* Period Selection Slider */}
        <Box className={styles.periodSlider}>
          <Text className={styles.periodLabel} mb="md">
            {`לתקופה של: ${recycledLoansComments.length > 0 ? '*' : ''}`}
          </Text>

          <AmountSlider
          value={onPeriodChange ? period : localPeriod}
          onChange={(value) => {
            const numericValue = Number(value.replace(/[^\d]/g, ''));
            if (onPeriodChange) {
              onPeriodChange(numericValue);
            } else {
              setLocalPeriod(numericValue);
            }
          }}
          min={minPeriod}
          max={maxPeriod}
          size="xl"
          step={1}
          showValue={false}
          showYearBox={true}
          yearBoxText={`${onPeriodChange ? period : localPeriod} שנים`}
          marksStep={5}
        />
        </Box>

        {/* Loan Details Section */}
        <Box className={styles.loanDetails}>
          {/* Initial Monthly Payment Row */}
          <Box className={styles.detailRowWithBorder}>
            <Text className={styles.detailLabel}>
              התשלום החודשי ההתחלתי:
            </Text>
            <Text className={styles.detailValue}>
              {format(monthlyPayment)} ₪
            </Text>
          </Box>

          {/* Total Repayment Row */}
          <Box className={styles.detailRowWithBorder}>
            <Text className={styles.detailLabel}>
              סך ההחזר הכולל לסוף התקופה:
            </Text>
            <Text className={styles.detailValue}>
              {format(totalPayment)} ₪
            </Text>
          </Box>

          {/* Repayment Ratio Row */}
          <Box className={styles.detailRow}>
            <Text className={styles.detailLabel}>
              עבור כל שקל הלקוחות ישלמו (יחס החזר)
            </Text>
            <Text className={styles.detailValue}>
              {format(returnConversion, false)} ₪
            </Text>
          </Box>
        </Box>
      </>
      )}
      {/* Savings Summary */}
      {selectedMix?.bankTotalReturn && selectedMix?.totalReturn && (
        <>
          <div className={styles.savingsCard}>
            <IconSparkles
              color='var(--mantine-color-green-6)'
              size='2rem'
              className={styles.sparkleIcon}
            />
            <Text className={styles.savingsTitle} ta="center">
              בהצעה זו אנחנו יכולים לחסוך ללקוחות כ -
              {' '}
              <Text span className={styles.savingsAmount}>
                {format(Math.round(Number(selectedMix.bankTotalReturn)
                  - Number(selectedMix.totalReturn) + additionalSavingsAmount))}* ₪
              </Text>
            </Text>
          </div>
          <Text pt={20} >הערות:</Text>
          <Text>
            * החיסכון המוצג הנו חסכון מצטבר בהשוואה להצעות ראשוניות מהבנקים בהגשת בקשות דומות.
          </Text>
          {recycledLoansComments.length > 0 && (
            <Box>
              {recycledLoansComments.map((comment, index) => (
                <Text key={index}>
                  {`* ${comment}`}
                </Text>
              ))}
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

MortgageRecommendation.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      risk: PropTypes.string,
      minPeriod: PropTypes.number,
      maxPeriod: PropTypes.number,
      mixes: PropTypes.arrayOf(
        PropTypes.shape({
          period: PropTypes.number,
          risk: PropTypes.number,
          creditPro: PropTypes.bool,
          utilizedRepaymentCapacity: PropTypes.number,
          totalReturn: PropTypes.number,
          returnConversion: PropTypes.number,
          bankTotalReturn: PropTypes.number,
        }),
      ),
    }),
  ).isRequired,
  period: PropTypes.number.isRequired,
  mortgagePaymentCapacity: PropTypes.number.isRequired,
  onPeriodChange: PropTypes.func.isRequired,
  loanAmount: PropTypes.number.isRequired,
  onRiskChange: PropTypes.func.isRequired,
  selectedRisk: PropTypes.string.isRequired,
  getMix: PropTypes.func.isRequired,
  morgageCycleDetails: PropTypes.object,
  proposal: PropTypes.object,
};

export default MortgageRecommendation;
